import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { WrenchIcon } from "lucide-react";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface Props {
  form: UseFormReturn<any, any, undefined>;
}

const predefinedViewportSizes = ["1920x1080", "1080x1920", "1920x1920"];

export const ViewportFormField: React.FC<Props> = ({ form }) => {
  const { t } = useTranslation();
  const [type, setType] = useState<"input" | "select">("select");

  return (
    <div className="flex flex-row gap-2 max-w-[300px]">
      {type === "input" ? (
        <FormField
          control={form.control}
          name="viewportSize"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>{t("template.viewportSize")}</FormLabel>
              <FormControl>
                <div className="flex flex-row gap-2 items-center">
                  <Input placeholder={t("common.example", { example: "1920x1080" })} {...field} />
                  <ToggleButton type={type} setType={setType} field={field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : (
        <FormField
          control={form.control}
          name="viewportSize"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>{t("template.viewportSize")}</FormLabel>
              <FormControl>
                <div className="flex flex-row gap-2 items-center">
                  <Select {...field} onValueChange={(value) => field.onChange(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {predefinedViewportSizes.map((size) => (
                        <SelectItem key={size} value={size}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ToggleButton type={type} setType={setType} field={field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};

interface ToggleButtonProps {
  type: "input" | "select";
  setType: (type: "input" | "select") => void;
  field: any;
}

const ToggleButton: React.FC<ToggleButtonProps> = ({ type, setType, field }) => {
  const { t } = useTranslation();
  return (
    <Tooltip>
      <TooltipTrigger>
        <span
          onClick={(e) => {
            setType(type === "input" ? "select" : "input");
            e.preventDefault();
            e.stopPropagation();
            field.onChange("");
          }}
          className="cursor-pointer"
        >
          <WrenchIcon className="w-4 h-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{type === "input" ? t("common.predefined") : t("common.manual")}</TooltipContent>
    </Tooltip>
  );
};
